import { useEffect, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { handleAutomationStudioWebSocketEvent } from '../automationStudioEventAdapter';

const GENERATION_EVENTS = [
    'log',
    'testStep',
    'screenshot',
    'tokenUsage',
    'hierarchicalSteps',
    'executionFlow',
    'testComplete',
    'error',
];

const REPLAY_EVENTS = [
    'replayStatus',
    'replayStepStart',
    'replayStepComplete',
    'replayComplete',
    'replayError',
    'replayLog',
];

/**
 * Bridges raw WebSocket events into automationStudioNew Redux via the event adapter.
 */
export default function useAutomationStudioNewSocketBridge({ on, isConnected, activeRunIdRef }) {
    const dispatch = useDispatch();
    const store = useStore();

    useEffect(() => {
        if (!isConnected || typeof on !== 'function') return undefined;

        const cleanups = [];

        const attach = (eventName) => {
            const cleanup = on(eventName, (payload) => {
                const isReplayEvent = REPLAY_EVENTS.includes(eventName);
                if (isReplayEvent) {
                    const sid = payload?.sessionId ?? payload?.uniqueId;
                    if (activeRunIdRef?.current && sid && sid !== activeRunIdRef.current) {
                        return;
                    }
                }

                const runId = activeRunIdRef?.current ?? payload?.runId ?? payload?.uniqueId ?? null;
                handleAutomationStudioWebSocketEvent(
                    dispatch,
                    store.getState,
                    {
                        type: eventName,
                        payload: {
                            ...(payload && typeof payload === 'object' ? payload : { message: payload }),
                            runId:
                                payload?.runId ??
                                payload?.uniqueId ??
                                payload?.sessionId ??
                                runId,
                            uniqueId:
                                payload?.uniqueId ??
                                payload?.sessionId ??
                                runId,
                            sessionId: payload?.sessionId ?? runId,
                        },
                    }
                );
            });
            if (typeof cleanup === 'function') cleanups.push(cleanup);
        };

        [...GENERATION_EVENTS, ...REPLAY_EVENTS].forEach(attach);

        return () => {
            cleanups.forEach((fn) => {
                try {
                    fn();
                } catch {
                    // ignore cleanup errors
                }
            });
        };
    }, [dispatch, store, on, isConnected, activeRunIdRef]);
}
